import { useCallback, useMemo, useRef, useState } from "react";
import { IntakeCameraSetup } from "./IntakeCameraSetup";
import { IntakeConsent } from "./IntakeConsent";
import { IntakeInterview } from "./IntakeInterview";
import { IntakeMatchResults } from "./IntakeMatchResults";
import { IntakeProfileGate } from "./IntakeProfileGate";
import { IntakeWrapUp } from "./IntakeWrapUp";
import { useIntakeProfile } from "./context/IntakeProfileContext";
import { useMediaStream } from "./hooks/useMediaStream";
import { useIntakeCopy } from "./hooks/useIntakeCopy";
import { IntakeShell } from "./components/IntakeShell";
import { InterviewPhase } from "./mocks/flowSteps";
import { resetFaceEmotions } from "./services/faceEmotionAccumulator";
import {
  consumeIntakeResultsStep,
  getIntakeSession,
  saveIntakeSession,
} from "./services/intakeMatchStorage";
import { buildMatchScoringInput } from "./services/sessionBuilder";
import { scoreAnswersWithRubrics } from "./services/rubricScoring";
import {
  getOrCreateSessionId,
  persistIntakeSession,
} from "./services/intakeSessionApi";
import { trackEvent } from "../../analytics/track";
import {
  IntakeAnswer,
  IntakeRole,
  IntakeStep,
  MatchProfile,
} from "./types/interviewSession";
import { MatchScoringInput } from "./services/matchScoring";

import "./IntakeFlow.css";

function buildMatchProfile(answers: IntakeAnswer[]): MatchProfile {
  const answered = answers.filter((a) => !a.skipped);
  const rubrics = scoreAnswersWithRubrics(answers);
  const avgScore =
    answered.length > 0
      ? answered.reduce((sum, a) => sum + a.sentiment.score, 0) / answered.length
      : 0.5;

  return {
    empathyScore: Math.round(avgScore * 100),
    accountabilityScore: rubrics.accountability,
    coachabilityScore: rubrics.coachability,
    narcissismFlags: rubrics.flags,
    energyType: avgScore > 0.5 ? "collaborative" : "reflective",
  };
}

function IntakeProcessing({ progress }: { progress: number }) {
  const { t, progressLabel } = useIntakeCopy();

  return (
    <IntakeShell
      progressStep={4}
      progressTotal={5}
      progressLabel={progressLabel(4, 5)}
      centerContent
    >
      <h1 className="intake-flow__headline">{t("intake.processing.headline")}</h1>
      <p className="intake-flow__subtext intake-flow__subtext--center">
        {t("intake.processing.subtext")}
      </p>
      <div className="intake-processing__bar">
        <div
          className="intake-processing__bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
    </IntakeShell>
  );
}

function IntakeFlowContent() {
  const { profile, isSurveyForm } = useIntakeProfile();
  const [step, setStep] = useState<IntakeStep>("consent");
  const [interviewPhase, setInterviewPhase] = useState<InterviewPhase>("main");
  const [hasCompletedDeeper, setHasCompletedDeeper] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [mainAnswers, setMainAnswers] = useState<IntakeAnswer[]>([]);
  const [deeperAnswers, setDeeperAnswers] = useState<IntakeAnswer[]>([]);
  const [restoredSession] = useState<MatchScoringInput | null>(() =>
    getIntakeSession()
  );

  const media = useMediaStream();
  const wrapUpVideoRef = useRef<HTMLVideoElement>(null);

  const profileContext = useMemo(
    () => ({
      occupationStatus: profile?.occupationStatus,
      isSurveyForm,
      profile,
    }),
    [profile, isSurveyForm]
  );

  const allAnswers = useMemo(
    () => [...mainAnswers, ...deeperAnswers],
    [mainAnswers, deeperAnswers]
  );

  const matchProfile = useMemo(
    () => (allAnswers.length > 0 ? buildMatchProfile(allAnswers) : null),
    [allAnswers]
  );

  const sessionInput = useMemo(() => {
    if (allAnswers.length === 0) {
      return restoredSession;
    }
    const role: IntakeRole = "mentee";
    const built = buildMatchScoringInput(allAnswers, role, profileContext);
    saveIntakeSession(built);
    return built;
  }, [allAnswers, restoredSession, profileContext]);

  const handleMainInterviewComplete = useCallback((answers: IntakeAnswer[]) => {
    setMainAnswers(answers);
    setInterviewPhase("main");
    setStep("wrap-up");
  }, []);

  const handleDeeperInterviewComplete = useCallback((answers: IntakeAnswer[]) => {
    setDeeperAnswers(answers);
    setHasCompletedDeeper(true);
    setStep("wrap-up");
  }, []);

  const handleMediaStart = useCallback(() => {
    void media.start();
  }, [media]);

  const startProcessing = useCallback(async () => {
    if (sessionInput && allAnswers.length > 0) {
      await persistIntakeSession({
        sessionId: getOrCreateSessionId(),
        journeyType: sessionInput.journeyType,
        role: sessionInput.role,
        answersJson: allAnswers,
        matchProfileJson: matchProfile,
        sessionInputJson: sessionInput,
        completed: false,
      });
    }
    setStep("processing");
    setProcessingProgress(0);
    const steps = [20, 45, 70, 100];
    for (const value of steps) {
      await new Promise((resolve) => setTimeout(resolve, 600));
      setProcessingProgress(value);
    }
    await new Promise((resolve) => setTimeout(resolve, 400));
    setStep("results");
  }, [allAnswers, matchProfile, sessionInput]);

  const handleGoDeeper = useCallback(() => {
    setInterviewPhase("deeper");
    setStep("interview");
  }, []);

  const handleConsent = useCallback(() => {
    resetFaceEmotions();
    getOrCreateSessionId();
    trackEvent("intake_start");
    setStep("camera");
  }, []);

  if (step === "consent") {
    return <IntakeConsent onAgree={handleConsent} />;
  }

  if (step === "camera") {
    return (
      <IntakeCameraSetup
        stream={media.stream}
        error={media.error}
        videoActive={media.videoActive}
        audioActive={media.audioActive}
        isLoading={media.isLoading}
        onStart={handleMediaStart}
        onContinue={() => {
          setInterviewPhase("main");
          setStep("interview");
        }}
        onRetry={() => void media.start()}
        onBack={() => setStep("consent")}
      />
    );
  }

  if (step === "interview") {
    return (
      <IntakeInterview
        key={interviewPhase}
        stream={media.stream}
        phase={interviewPhase}
        onComplete={
          interviewPhase === "deeper"
            ? handleDeeperInterviewComplete
            : handleMainInterviewComplete
        }
        onBackToCamera={() => setStep("camera")}
        onBackToWrapUp={() => setStep("wrap-up")}
      />
    );
  }

  if (step === "wrap-up") {
    return (
      <IntakeWrapUp
        stream={media.stream}
        videoRef={wrapUpVideoRef}
        variant={hasCompletedDeeper ? "after_deeper" : "initial"}
        onBack={() => {
          setInterviewPhase(hasCompletedDeeper ? "deeper" : "main");
          setStep("interview");
        }}
        onPreview={() => void startProcessing()}
        onGoDeeper={hasCompletedDeeper ? undefined : handleGoDeeper}
      />
    );
  }

  if (step === "processing") {
    return <IntakeProcessing progress={processingProgress} />;
  }

  return (
    <IntakeMatchResults sessionInput={sessionInput} onBack={() => setStep("wrap-up")} />
  );
}

export function IntakeFlow() {
  const skipProfileGate = consumeIntakeResultsStep();
  const [initialStep] = useState<IntakeStep | null>(
    skipProfileGate ? "results" : null
  );

  if (initialStep === "results") {
    const restoredSession = getIntakeSession();
    return (
      <IntakeProfileGate skipGate>
        <IntakeMatchResults sessionInput={restoredSession} />
      </IntakeProfileGate>
    );
  }

  return (
    <IntakeProfileGate>
      <IntakeFlowContent />
    </IntakeProfileGate>
  );
}
